// F3 flow-first "Add Integration" picker — curated flow-type vocabulary + runtime helpers.
//
// The catalog's raw flow entries carry 63 distinct display names across 59 id codes, and the
// mapping between them is many-to-many (see docs/f3-flow-type-picker-analysis.md). This module
// curates them down to the 38 customer-facing flow types the left panel offers (see
// docs/f3-flow-type-curated-mapping.md), and derives everything the right panel needs from the
// LIVE catalog — integration coverage is never hardcoded here.
//
// Each catalog flow id is `<sku>.<code>`. `resolveCuratedType` maps that id to a curated type via a
// code-level default map plus explicit per-id exceptions (only `vis.send_milestone`, which is a
// request for the latest events rather than a status publish). The default map + the one exception
// were derived from, and verified byte-for-byte against, the approved prototype's fixtures
// (docs/ux-experiments/add-integration-flow-first-2026-09-22.html) — the module reproduces the
// prototype's exact type set and id->type assignments (the two Retrieve types were later merged
// into their Receive counterparts and the section renamed to "Receive", Charlie 2026-09-22).

import { isFlatVisualBuilderSku, LEGACY_VISUAL_BUILDER_SKU } from '../../utils/visualBuilderSku'

// Render order for the left panel's groups. Generic (pass-through) is forced LAST: it applies to
// almost every integration, so it must not lead.
export const SECTION_ORDER = ['Send', 'Receive', 'Generic']

// The 38 curated flow types, in curation order, each tagged with its section.
export const CURATED_FLOW_TYPES = [
  { name: 'Receive Data', section: 'Generic' },
  { name: 'Send Data', section: 'Generic' },
  { name: 'Register Shipment', section: 'Send' },
  { name: 'Request Shipment Updates', section: 'Send' },
  { name: 'Send Booking Confirmations', section: 'Send' },
  { name: 'Send Bookings', section: 'Send' },
  { name: 'Send Coverage Requests', section: 'Send' },
  { name: 'Send Documents', section: 'Send' },
  { name: 'Send Inventory Receipts', section: 'Send' },
  { name: 'Send Inventory Status', section: 'Send' },
  { name: 'Send Invoices', section: 'Send' },
  { name: 'Send Milestones', section: 'Send' },
  { name: 'Send Organizations', section: 'Send' },
  { name: 'Send Product Master', section: 'Send' },
  { name: 'Send Purchase Orders', section: 'Send' },
  { name: 'Send Rate Requests', section: 'Send' },
  { name: 'Send Shipments & ASNs', section: 'Send' },
  { name: 'Send Shipping Instructions', section: 'Send' },
  { name: 'Send VGM', section: 'Send' },
  { name: 'Receive Booking Acknowledgments', section: 'Receive' },
  { name: 'Receive Booking Confirmations', section: 'Receive' },
  { name: 'Receive Bookings', section: 'Receive' },
  { name: 'Receive CO2 Updates', section: 'Receive' },
  { name: 'Receive Commercial Invoices', section: 'Receive' },
  { name: 'Receive Documents', section: 'Receive' },
  { name: 'Receive Fulfillment Orders', section: 'Receive' },
  { name: 'Receive Insurance Status Updates', section: 'Receive' },
  { name: 'Receive Invoices', section: 'Receive' },
  { name: 'Receive Milestones / Events', section: 'Receive' },
  { name: 'Receive Organizations', section: 'Receive' },
  { name: 'Receive Payment Receipts', section: 'Receive' },
  { name: 'Receive Product Master', section: 'Receive' },
  { name: 'Receive Purchase Orders', section: 'Receive' },
  { name: 'Receive Rate Master', section: 'Receive' },
  { name: 'Receive Rate Responses', section: 'Receive' },
  { name: 'Receive Security Filings', section: 'Receive' },
  { name: 'Receive Shipments', section: 'Receive' },
  { name: "Receive Shipper's Instructions", section: 'Receive' }
]

// Display label overrides for a section heading. Only Generic is relabelled for customers.
export const SECTION_LABELS = { Generic: 'Any data (pass-through)' }

// code (the part of a flow id after `<sku>.`) -> curated type name. Covers every non-v3 catalog
// flow code today. New catalog codes that are not here resolve to null (unmapped) — see
// getUnmappedFlowCodes / the dialog's dev warning.
const DEFAULT_TYPE_BY_CODE = {
  send_passthrough: 'Send Data',
  receive_passthrough: 'Receive Data',
  register_shipment: 'Register Shipment',
  receive_shipment_update: 'Receive Milestones / Events',
  send_milestone: 'Send Milestones',
  send_asn: 'Send Shipments & ASNs',
  receive_co2_update: 'Receive CO2 Updates',
  receive_booking: 'Receive Bookings',
  send_booking_confirmation: 'Send Booking Confirmations',
  receive_shippers_instructions: "Receive Shipper's Instructions",
  receive_security_filing: 'Receive Security Filings',
  receive_document: 'Receive Documents',
  receive_invoice: 'Receive Invoices',
  send_invoice: 'Send Invoices',
  receive_milestones: 'Receive Milestones / Events',
  send_document: 'Send Documents',
  receive_product_master: 'Receive Product Master',
  send_booking: 'Send Bookings',
  receive_booking_response: 'Receive Booking Confirmations',
  send_shippers_instructions: 'Send Shipping Instructions',
  receive_milestone: 'Receive Milestones / Events',
  receive_asn: 'Receive Shipments',
  send_job: 'Send Shipments & ASNs',
  send_organization: 'Send Organizations',
  receive_payment_receipt: 'Receive Payment Receipts',
  // Retrieve flows merged into their Receive counterparts (Charlie 2026-09-22).
  retrieve_shipment: 'Receive Shipments',
  retrieve_organization: 'Receive Organizations',
  receive_booking_acknowledgment: 'Receive Booking Acknowledgments',
  send_shipping_instructions: 'Send Shipping Instructions',
  send_vgm: 'Send VGM',
  receive_shipment: 'Receive Shipments',
  receive_commercial_invoice: 'Receive Commercial Invoices',
  send_shipment: 'Send Shipments & ASNs',
  send_purchase_order: 'Send Purchase Orders',
  receive_organization: 'Receive Organizations',
  send_product_master: 'Send Product Master',
  receive_purchase_order: 'Receive Purchase Orders',
  send_coverage_request: 'Send Coverage Requests',
  receive_insurance_certificate: 'Receive Documents',
  receive_referral_notification: 'Receive Insurance Status Updates',
  receive_rejection_notification: 'Receive Insurance Status Updates',
  receive_insurance_status_update: 'Receive Insurance Status Updates',
  send_shipment_rate_request: 'Send Rate Requests',
  receive_shipment_rate_response: 'Receive Rate Responses',
  receive_rate_master: 'Receive Rate Master',
  receive_fulfillment_order: 'Receive Fulfillment Orders',
  send_inventory_receipts: 'Send Inventory Receipts',
  send_inventory_status: 'Send Inventory Status',
  shipper_outbound_asn_to_3rd_party: 'Send Shipments & ASNs',
  shipper_outbound_milestones_to_3rd_party: 'Send Milestones',
  shipper_inbound_asn_from_3rd_party: 'Receive Shipments',
  shipper_inbound_milestones_from_3rd_party: 'Receive Milestones / Events',
  transmit_booking_requests_to_carrier: 'Send Bookings',
  transmit_cargo_shipping_instructions_to_carrier: 'Send Shipping Instructions',
  send_vgm_details: 'Send VGM',
  receive_invoices: 'Receive Invoices',
  receive_booking_confirmations_details: 'Receive Booking Confirmations',
  receive_events: 'Receive Milestones / Events'
}

// Full-id exceptions that override the code default. `send_milestone` is "Send Milestones"
// everywhere except `vis.send_milestone`, which requests the latest events from a visibility
// provider ("Request Shipment Updates").
const TYPE_BY_ID = {
  'vis.send_milestone': 'Request Shipment Updates'
}

const NAME_ORDER = new Map(CURATED_FLOW_TYPES.map((type, index) => [type.name, index]))

const codeFromId = flowId => {
  if (typeof flowId !== 'string') return null
  const dot = flowId.indexOf('.')
  return dot === -1 ? null : flowId.slice(dot + 1)
}

// Resolve a catalog flow id (`<sku>.<code>`) to its curated flow-type name, or null when the flow
// is not part of the curated vocabulary (e.g. the Visual Builder `v3.v3` placeholder).
export const resolveCuratedType = flowId => {
  if (TYPE_BY_ID[flowId]) return TYPE_BY_ID[flowId]
  const code = codeFromId(flowId)
  if (code === null) return null
  return DEFAULT_TYPE_BY_CODE[code] || null
}

// The catalog integrations the picker offers on the right — every integration type except the
// legacy Visual Builder SKU (`v3`). The flat-priced `vb` SKU is offered; its single flow (`vb.vb`)
// is not a curated type, so its card has no chips and its unmapped code is never surfaced as drift
// (see getUnmappedFlowCodes). This is the denominator behind the match counter.
export const selectableIntegrations = catalog =>
  (catalog?.integrations || []).filter(integration => integration.id !== LEGACY_VISUAL_BUILDER_SKU)

// The curated types an integration supports, as curated names, ordered by curation order.
export const supportedTypesForIntegration = integration => {
  const names = new Set()
  ;(integration?.flows || []).forEach(flow => {
    const type = resolveCuratedType(flow.id)
    if (type) names.add(type)
  })
  return [...names].sort((a, b) => NAME_ORDER.get(a) - NAME_ORDER.get(b))
}

// The selectable integrations that support a given curated type.
export const integrationsForType = (catalog, curatedName) =>
  selectableIntegrations(catalog).filter(integration =>
    (integration.flows || []).some(flow => resolveCuratedType(flow.id) === curatedName))

// The ordered, grouped set of curated types actually present in the live catalog. Groups follow
// SECTION_ORDER (Generic last); types within a group follow curation order. Types no catalog
// integration supports are omitted so the panel never offers an empty option.
export const curatedGroupsInCatalog = catalog => {
  const present = new Set()
  selectableIntegrations(catalog).forEach(integration =>
    supportedTypesForIntegration(integration).forEach(name => present.add(name)))

  return SECTION_ORDER
    .map(section => ({
      section,
      label: SECTION_LABELS[section] || section,
      types: CURATED_FLOW_TYPES.filter(type => type.section === section && present.has(type.name)).map(type => type.name)
    }))
    .filter(group => group.types.length > 0)
}

// The integration's catalog flows (full entries: name, description, max_flows, ...) that roll up
// into a given curated type — what a picker chip's detail popover describes. Catalog order.
export const catalogFlowsForType = (integration, curatedName) =>
  (integration?.flows || []).filter(flow => resolveCuratedType(flow.id) === curatedName)

// Catalog flow codes that resolve to no curated type — drift markers for new catalog entries. The
// integration stays selectable; the flow is just not offered as a left-panel option.
export const getUnmappedFlowCodes = catalog => {
  const codes = new Set()
  selectableIntegrations(catalog).filter(integration => !isFlatVisualBuilderSku(integration.id)).forEach(integration =>
    (integration.flows || []).forEach(flow => {
      if (!resolveCuratedType(flow.id)) {
        codes.add(codeFromId(flow.id) ?? String(flow.id))
      }
    }))
  return [...codes].sort()
}

let warnedSignature = null

// Dev-only: warn once (per distinct set of codes) that some catalog flows are unmapped, so drift
// from new integrationLicenseTypes entries is visible. Never throws.
export const warnUnmappedFlowCodesOnce = catalog => {
  const isProd = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD
  if (isProd) return
  const codes = getUnmappedFlowCodes(catalog)
  if (codes.length === 0) return
  const signature = codes.join(',')
  if (signature === warnedSignature) return
  warnedSignature = signature
  // eslint-disable-next-line no-console
  console.warn(`[layoutBuilder] Add Integration picker: ${codes.length} catalog flow code(s) are not in the curated flow-type map and will not be offered as filters: ${signature}`)
}

// Test-only: reset the once-per-signature guard above.
export const resetUnmappedFlowWarning = () => { warnedSignature = null }
